import React, { useState, useEffect, useMemo } from 'react';
import * as apiClient from '../services/apiClient';
import { 
  X, 
  Link2, 
  Search, 
  Building2, 
  CreditCard, 
  CheckCircle2, 
  Sparkles, 
  Filter, 
  Gift, 
  Plane,
  ChevronRight
} from 'lucide-react';

/**
 * ConnectCardModal
 * Spacious, full-featured directory browser modal for connecting custom/unlinked cards
 * to the UAE Credit Cards Catalog.
 */
export default function ConnectCardModal({
  isOpen,
  account,
  targetFeature = null, // 'community' | 'rewards' | 'manage'
  onClose,
  onConnected,
  showToast
}) {
  const [cards, setCards] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedCardId, setSelectedCardId] = useState(account?.master_card_id || null);
  const [selectedCardObj, setSelectedCardObj] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch catalog on modal open
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    async function loadCatalog() {
      setLoading(true);
      try {
        const [banksRes, cardsRes] = await Promise.all([
          apiClient.request('/catalog/banks?country=AE'),
          apiClient.request('/catalog/cards?country=AE&limit=250&sort=name_asc')
        ]);
        if (isMounted) {
          const loadedCards = cardsRes?.cards || [];
          setBanks(Array.isArray(banksRes) ? banksRes : []);
          setCards(loadedCards);

          // If preselected card id exists, resolve card object
          if (account?.master_card_id) {
            const found = loadedCards.find(c => c.card_id === account.master_card_id);
            if (found) {
              setSelectedCardId(found.card_id);
              setSelectedCardObj(found);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load directory for connect modal:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadCatalog();
    return () => { isMounted = false; };
  }, [isOpen, account?.master_card_id]);

  // Filtered cards calculation
  const filteredCards = useMemo(() => {
    return cards.filter(card => {
      if (selectedBank && card.bank_name !== selectedBank) return false;
      if (searchTerm && searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = (card.card_name || '').toLowerCase().includes(q);
        const matchBank = (card.bank_name || '').toLowerCase().includes(q);
        const matchTier = (card.card_tier || '').toLowerCase().includes(q);
        const matchPerks = (card.unique_perks_desc || '').toLowerCase().includes(q);
        return matchName || matchBank || matchTier || matchPerks;
      }
      return true;
    });
  }, [cards, selectedBank, searchTerm]);

  if (!isOpen || !account) return null;

  const handleSelectCard = (card) => {
    if (selectedCardId === card.card_id) {
      setSelectedCardId(null);
      setSelectedCardObj(null);
    } else {
      setSelectedCardId(card.card_id);
      setSelectedCardObj(card);
    }
  };

  const handleConnect = async () => {
    if (!selectedCardId) {
      if (showToast) showToast('Please select a card from the directory list', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.request(`/accounts/${account.account_id}`, 'PUT', {
        name: account.name,
        nickname: account.nickname || account.name,
        master_card_id: selectedCardId,
        current_balance: account.current_balance,
        include_in_total: account.include_in_total,
        credit_limit: account.credit_limit,
        statement_day: account.statement_day,
        due_day: account.due_day,
        monthly_spend_target: account.monthly_spend_target,
        monthly_spend_target_type: account.monthly_spend_target_type,
        is_active: account.is_active !== false && account.is_active !== 0 && account.is_active !== '0'
      });

      if (showToast) showToast('Card connected to directory successfully!', 'success');

      const updatedAccount = {
        ...account,
        master_card_id: selectedCardId,
        master_card_name: selectedCardObj?.card_name || account.master_card_name,
        master_bank_name: selectedCardObj?.bank_name || account.master_bank_name,
        master_card_tier: selectedCardObj?.card_tier || account.master_card_tier,
        master_logo_url: selectedCardObj?.logo_url || account.master_logo_url
      };

      if (onConnected) {
        onConnected(updatedAccount, targetFeature);
      }
      onClose();
    } catch (err) {
      if (showToast) showToast(err.message || 'Failed to connect card', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getActionBtnText = () => {
    if (isSubmitting) return 'Connecting...';
    if (targetFeature === 'community') return 'Connect & Open Community';
    if (targetFeature === 'rewards') return 'Connect & View Rewards';
    return 'Connect to Directory';
  };

  return (
    <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        className="glass-panel modal-spring-enter modal-content-panel"
        style={{
          width: '95%',
          maxWidth: '860px',
          height: '86vh',
          maxHeight: '760px',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '20px',
          overflow: 'hidden',
          background: 'var(--bg-main, #0f111a)',
          border: '1px solid var(--border-glass)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
          boxSizing: 'border-box'
        }}
      >
        {/* Top Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid var(--border-glass)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.02)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'rgba(165,94,234,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--accent-purple)'
              }}
            >
              <Link2 size={22} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                Connect to Card Directory
                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: 'rgba(0, 245, 212, 0.12)', color: 'var(--accent-cyan)', fontWeight: '600' }}>
                  🇦🇪 UAE Directory
                </span>
              </h2>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Connecting: <strong style={{ color: 'var(--text-main)' }}>{account.name}</strong>
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Discovery & Search Controls */}
        <div
          style={{
            padding: '14px 24px',
            borderBottom: '1px solid var(--border-glass)',
            background: 'rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {/* Search Input */}
            <div style={{ position: 'relative', flex: 1, minWidth: '240px' }}>
              <Search
                size={16}
                color="var(--text-muted)"
                style={{ position: 'absolute', left: '12px', top: '12px' }}
              />
              <input
                type="text"
                placeholder="Search by card name, bank, or benefits (e.g. 365, Skywards, Vox)..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px 10px 38px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  fontSize: '13px',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>

            {/* Bank Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '200px' }}>
              <Building2 size={16} color="var(--text-muted)" style={{ flexShrink: 0 }} />
              <select
                value={selectedBank}
                onChange={e => setSelectedBank(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  fontSize: '13px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">All Banks ({banks.length})</option>
                {banks.map(bank => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick Info & Count Strip */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: 'var(--text-muted)' }}>
            <span>
              Showing {filteredCards.length} verified credit cards
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Sparkles size={12} color="var(--accent-purple)" />
              Select a card to activate rewards breakdown & community discussions
            </span>
          </div>
        </div>

        {/* Scrollable Card Selection Grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px' }}>
          {loading ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <Sparkles size={24} className="animate-spin" color="var(--accent-purple)" style={{ margin: '0 auto 12px' }} />
              <p style={{ margin: 0, fontSize: '14px' }}>Loading UAE Credit Cards Directory...</p>
            </div>
          ) : filteredCards.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <CreditCard size={36} style={{ opacity: 0.3, margin: '0 auto 12px' }} />
              <h4 style={{ margin: '0 0 6px', fontSize: '15px', color: 'var(--text-main)' }}>No matching cards found</h4>
              <p style={{ margin: 0, fontSize: '12px' }}>Try searching by bank name or clear the bank filter.</p>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '12px'
              }}
            >
              {filteredCards.map(card => {
                const isSelected = card.card_id === selectedCardId;
                return (
                  <div
                    key={card.card_id}
                    onClick={() => handleSelectCard(card)}
                    style={{
                      padding: '14px',
                      borderRadius: '14px',
                      border: isSelected 
                        ? '2px solid var(--accent-cyan)' 
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      background: isSelected 
                        ? 'rgba(0, 245, 212, 0.08)' 
                        : 'rgba(255, 255, 255, 0.02)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '10px',
                      transition: 'all 0.15s ease',
                      position: 'relative',
                      boxShadow: isSelected ? '0 4px 20px rgba(0, 245, 212, 0.15)' : 'none'
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                      }
                    }}
                  >
                    <div>
                      {/* Top Row: Bank Name + Selected Check */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          {card.bank_name}
                        </span>
                        {isSelected && (
                          <CheckCircle2 size={18} color="var(--accent-cyan)" style={{ flexShrink: 0 }} />
                        )}
                      </div>

                      {/* Card Title */}
                      <h4 style={{ margin: '0 0 6px', fontSize: '13.5px', fontWeight: '700', color: isSelected ? 'var(--accent-cyan)' : 'var(--text-main)', lineHeight: '1.4' }}>
                        {card.card_name}
                      </h4>

                      {/* Perks Preview */}
                      {card.unique_perks_desc && (
                        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {card.unique_perks_desc}
                        </p>
                      )}
                    </div>

                    {/* Bottom Metadata Badges */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: '10.5px' }}>
                      <span style={{ padding: '2px 6px', borderRadius: '4px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)' }}>
                        {card.card_tier || 'Standard'}
                      </span>
                      <span style={{ color: card.annual_fee === 0 ? 'var(--accent-cyan)' : 'var(--text-muted)', fontWeight: '600' }}>
                        {card.annual_fee === 0 ? 'Free for Life' : `Fee: AED ${card.annual_fee}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sticky Bottom Action Bar */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid var(--border-glass)',
            background: 'var(--bg-secondary, #141724)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}
        >
          <div style={{ flex: 1, minWidth: '220px' }}>
            {selectedCardObj ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle2 size={16} color="var(--accent-cyan)" />
                <div style={{ fontSize: '12px', lineHeight: '1.3' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Selected: </span>
                  <strong style={{ color: 'var(--accent-cyan)' }}>{selectedCardObj.card_name}</strong>
                  <span style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>({selectedCardObj.bank_name})</span>
                </div>
              </div>
            ) : (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Click any card above to select it for connection.
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              style={{ padding: '10px 18px', fontSize: '12.5px', borderRadius: '10px' }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedCardId || isSubmitting}
              onClick={handleConnect}
              className="btn-primary"
              style={{
                padding: '10px 22px',
                fontSize: '12.5px',
                fontWeight: '700',
                borderRadius: '10px',
                opacity: (!selectedCardId || isSubmitting) ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Link2 size={14} />
              {getActionBtnText()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
